import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Icon, SelectComplexItem, SelectSelection } from '@circlek-global/lds-components';
import { BehaviorSubject, catchError, debounceTime, EMPTY, filter, map, Observable, of, switchMap, tap } from 'rxjs';

import { GEOLOCATION_ZOOM } from '~constants/map.constants';
import { environment } from '~environments/environment.development';
import { LOCAL_STORAGE_KEYS, LocalStorageService } from '~services/local-storage.service';
import { MapService } from '~services/map-service';
import { StationsService } from '~services/station.service';
import { TranslationService } from '~services/translation.service';
import { BusinessUnit, DEFAULT_BUSINESS_UNIT } from '~shared/business-unit';
import { getCountryFromBu } from '~utils/business-unit.utils';
import { sentryError } from '~utils/sentry-error';

const AUTOCOMPLETE_HISTORY_LIMIT = 5;

@Injectable({
  providedIn: 'root',
})
export class AutocompleteService {
  readonly suggestions = signal<SelectComplexItem[]>([]);
  readonly search$ = new BehaviorSubject('');
  readonly autocompleteSelect = new FormControl<SelectSelection>(null);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationService = inject(TranslationService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly mapService = inject(MapService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly stationsService = inject(StationsService);
  readonly NEAREST_SUGGESTION: SelectComplexItem = {
    id: 'NEAREST_SUGGESTION',
    text: this.translationService.translate('findNearest'),
    optionIcon: Icon.MARKER_PIN_04,
  };
  private readonly bu = (this.activatedRoute.snapshot.queryParams['bu'] ?? DEFAULT_BUSINESS_UNIT) as BusinessUnit;

  constructor() {
    this.getSearchAutocompleteResults().subscribe();

    this.navigateOnQueryParamSearch().subscribe();
  }

  getSearchAutocompleteResults(): Observable<SelectComplexItem[]> {
    return this.search$.pipe(
      takeUntilDestroyed(this.destroyRef),
      debounceTime(300),
      switchMap((search) => {
        if (search == null || search.trim() === '') {
          const key = LOCAL_STORAGE_KEYS.suggestions;
          const initialSuggestions = this.localStorageService.getItem<SelectComplexItem[]>(key) ?? [];

          return of(initialSuggestions);
        }

        return this.getSuggestionsAutocomplete(search);
      }),
      tap((suggestionsResult) => {
        this.suggestions.set(suggestionsResult);
      }),
    );
  }

  //TODO(WFE-2679): after implementing translations, test US region languageCode.
  getSuggestionsAutocomplete(search: string): Observable<SelectComplexItem[]> {
    const country = getCountryFromBu(this.bu).toUpperCase();
    const languageCode = this.translationService.getLanguage();

    return this.http
      .post<{ suggestions: google.maps.places.AutocompleteSuggestion[] }>(
        'https://places.googleapis.com/v1/places:autocomplete',
        {
          input: search,
          includedPrimaryTypes: ['locality', 'route'],
          languageCode,
          includedRegionCodes: [country],
        },
        {
          headers: {
            'X-Goog-Api-Key': environment.googleApiKey,
          },
        },
      )
      .pipe(
        catchError((error) => {
          sentryError(error, 'Google Places', 'Autocomplete error', { search, languageCode, country });

          return [];
        }),
        map((results) => {
          if (results.suggestions == null || results.suggestions.length === 0) {
            return [];
          }

          const mappedSuggestions = results.suggestions
            .map((s) => s.placePrediction)
            .filter((placePrediction) => placePrediction != null)
            .map((placePrediction) => ({
              id: placePrediction.placeId,
              text: placePrediction.text.text,
              optionIcon: Icon.MARKER_PIN_01,
            }));

          return mappedSuggestions;
        }),
      );
  }

  navigateOnSuggestion(placeId: string): Observable<void> {
    return this.http
      .get<{ location: { latitude: number; longitude: number } }>(
        `https://places.googleapis.com/v1/places/${placeId}?fields=location`,
        {
          headers: {
            'X-Goog-Api-Key': environment.googleApiKey,
          },
        },
      )
      .pipe(
        catchError((error) => {
          sentryError(error, 'Google Places', 'Details API error', { placeId });

          return EMPTY;
        }),
        tap(({ location }) => {
          const { latitude, longitude } = location;

          this.mapService.mapConfig.set({ center: { lat: latitude, lng: longitude }, zoom: 13 });
        }),
        map(() => undefined),
      );
  }

  saveSuggestionToLocalStorage(suggestion: SelectComplexItem): void {
    const key = LOCAL_STORAGE_KEYS.suggestions;
    const oldSuggestions = this.localStorageService.getItem<SelectComplexItem[]>(key) ?? [];

    if (oldSuggestions.some((s) => s.id === suggestion.id)) {
      const filteredSuggestions = oldSuggestions.filter((s) => s.id !== suggestion.id);
      const newSuggestions = [suggestion, ...filteredSuggestions];

      this.localStorageService.setItem(key, newSuggestions);
      this.suggestions.set(newSuggestions);

      return;
    }

    oldSuggestions.unshift(suggestion);
    const newSuggestions = oldSuggestions.slice(0, AUTOCOMPLETE_HISTORY_LIMIT);

    this.localStorageService.setItem(key, newSuggestions);
    this.suggestions.set(newSuggestions);
  }

  navigateOnQueryParamSearch(): Observable<void> {
    const search = this.activatedRoute.snapshot.queryParams['search'];

    if (!search) {
      return EMPTY;
    }

    return this.getSuggestionsAutocomplete(search).pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((suggestions) => suggestions.length > 0),
      map((suggestions) => suggestions[0] as SelectComplexItem & { id: string }),
      tap((firstSuggestion) => {
        this.saveSuggestionToLocalStorage(firstSuggestion);
        this.autocompleteSelect.setValue(firstSuggestion);
      }),
      switchMap((firstSuggestion) => this.navigateOnSuggestion(firstSuggestion.id)),
    );
  }

  findNearestStation(): void {
    this.mapService.navigationLoading.set(true);
    this.mapService.getUserPosition((position) => {
      this.stationsService.getNearestStation(position).subscribe((station) => {
        const { latitude, longitude } = station;

        this.mapService.navigateOnDemand({ lat: latitude, lng: longitude }, GEOLOCATION_ZOOM);
        this.autocompleteSelect.setValue(null);
        this.stationsService.selectStation(station);
        this.mapService.navigationLoading.set(false);
      });
    });
  }
}
